import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { money, formatErr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Save, Tag, AlertTriangle } from "lucide-react";

function PlanCard({ plan, onSaved }) {
    const [form, setForm] = useState({
        name: plan.name || "", tagline: plan.tagline || "",
        price: plan.price ?? 0, features: (plan.features || []).join("\n"),
        active: plan.active !== false,
    });
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            const payload = {
                name: form.name, tagline: form.tagline, price: Number(form.price),
                features: form.features.split("\n").map((f) => f.trim()).filter(Boolean),
                active: form.active,
            };
            const { data } = await api.put(`/admin/plans/${plan.lookup_key}`, payload);
            if (data.stripe_synced === false) {
                toast.warning(`Saved, but Stripe price didn't update: ${data.stripe_error || "unknown error"}`);
            } else {
                toast.success(`${form.name} updated`);
            }
            onSaved?.();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="bg-white border border-border rounded-xl p-5 flex flex-col" data-testid={`plan-editor-${plan.lookup_key}`}>
            <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-xs font-mono text-brand-teal"><Tag className="h-3.5 w-3.5" />{plan.lookup_key}</span>
                <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                    Active
                    <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid={`plan-active-${plan.lookup_key}`} />
                </label>
            </div>
            <div className="mt-3 space-y-3">
                <div><Label className="text-xs">Plan name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid={`plan-name-${plan.lookup_key}`} /></div>
                <div><Label className="text-xs">Tagline</Label><Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="mt-1" data-testid={`plan-tagline-${plan.lookup_key}`} /></div>
                <div>
                    <Label className="text-xs">Price (USD / {plan.interval || "year"})</Label>
                    <div className="mt-1 flex items-center gap-2">
                        <span className="text-muted-foreground">$</span>
                        <Input type="number" min="0" step="1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="font-mono" data-testid={`plan-price-${plan.lookup_key}`} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Currently {money(plan.price)}. Changing this creates a new Stripe price for future checkouts.</p>
                </div>
                <div>
                    <Label className="text-xs">Features (one per line)</Label>
                    <Textarea rows={5} value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} className="mt-1" data-testid={`plan-features-${plan.lookup_key}`} />
                </div>
            </div>
            <div className="mt-4 flex justify-end">
                <Button disabled={busy} onClick={save} className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid={`plan-save-${plan.lookup_key}`}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save plan</>}
                </Button>
            </div>
        </div>
    );
}

export function PlansManager() {
    const [plans, setPlans] = useState(null);
    const load = () => api.get("/admin/plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
    useEffect(() => { load(); }, []);

    return (
        <div className="space-y-5" data-testid="plans-manager">
            <div>
                <h2 className="text-lg font-semibold text-brand-emerald">Subscription Plans</h2>
                <p className="text-sm text-muted-foreground mt-1">Edit the price, tagline and feature list shown to schools on the Billing page.</p>
            </div>
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-xs px-3 py-2.5" data-testid="plans-price-note">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Price changes apply to <b>new checkouts</b>. Schools already subscribed keep their current Stripe price until they renew or resubscribe.</span>
            </div>
            {plans === null ? (
                <div className="grid place-items-center py-12 text-brand-emerald"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : (
                <div className="grid md:grid-cols-2 gap-5">
                    {plans.map((p) => <PlanCard key={p.lookup_key} plan={p} onSaved={load} />)}
                </div>
            )}
        </div>
    );
}
